import styled, { css } from 'styled-components';
import { mobile } from '../mixins/media';
import type { ModalBodyVariant } from '../ModalOverlay/type';

const DEFAULT_DESKTOP_MAX_HEIGHT = '80vh';

interface BodyProps {
    height?: string;
    minHeight?: string;
    width?: string;
    margin?: string;
    variant?: ModalBodyVariant;
}

const getHeightValues = (minHeight: number) => {
    const values = {
        limit: minHeight,
        newMinHeight: 'auto',
    };

    const relativeMaxHeight = Number(DEFAULT_DESKTOP_MAX_HEIGHT.replace(/vh|%/, ''));

    if (relativeMaxHeight) {
        values.limit = (minHeight / relativeMaxHeight) * 100;
        values.newMinHeight = DEFAULT_DESKTOP_MAX_HEIGHT;
    }

    return values;
};

const getMinHeightStyles = (minHeight: string) => {
    let mediaQueryStyles;
    const minHeightNumber = Number(minHeight.replace('px', ''));

    if (minHeightNumber) {
        const { limit, newMinHeight } = getHeightValues(minHeightNumber);
        mediaQueryStyles = css`
            @media (max-height: ${limit}px) {
                min-height: ${newMinHeight};
            }
        `;
    }

    return css`
        min-height: ${minHeight};
        ${mediaQueryStyles}
    `;
};

export const ModalBody = styled.section<BodyProps>`
    position: relative;
    width: ${props => props.width || '500px'};
    max-height: ${DEFAULT_DESKTOP_MAX_HEIGHT};
    ${props => props.minHeight && getMinHeightStyles(props.minHeight)};
    height: ${props => props.height};
    margin: ${props => props.margin ?? '10vh auto'};
    box-shadow: ${props => props.theme.modal.boxShadow};
    background: ${props => props.theme.modal.bgColorWithoutOpacity};
    box-sizing: border-box;
    border-radius: 24px;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    ${mobile<BodyProps>`
        justify-content: center;
        width: 100%;
        max-height: 100vh;
        height: 100vh;
        box-shadow: none;
        border-radius: 0;
    `}

    ${props =>
        props.variant === 'full-width' &&
        css`
            height: 100vh;
            max-height: 100vh;
            width: 100%;
            margin: 0;
            justify-content: flex-start;
            padding: 0;
            border-radius: 0;
            box-shadow: none;
            background: transparent;
        `}

    ${props =>
        props.variant === 'full-height' &&
        css`
            height: 80vh;
        `}

    ${props =>
        props.variant === 'fit-content' &&
        css`
            ${mobile<BodyProps>`
                height: auto;
            `}
        `}
`;

export const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0 24px;
    flex: 1;
    min-height: 0;
    min-width: 0;
    overflow-x: hidden;
    overflow-y: auto;
`;

export const ModalHeaderWithContentWrapper = styled.div`
    z-index: 0;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    min-width: 0;
    overflow: hidden;
`;

export const ModalForm = styled.form`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    max-height: 100%;
    overflow: hidden;
`;

export const ModalFooter = styled.div`
    display: flex;
    flex-direction: row;
    gap: 8px;
    align-items: center;
    justify-content: flex-end;
    padding: 24px;
`;
